import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import { ToastProvider } from "./components/ui";
import Landing from "./pages/landing/Landing";
import { match, usePath } from "./router";
import { SessionProvider } from "./session";

/* Everything except the landing page is code-split so the first paint stays light. */
const pages = {
  signUp: lazy(() => import("./pages/auth/AuthPages").then((m) => ({ default: m.SignUp }))),
  signIn: lazy(() => import("./pages/auth/AuthPages").then((m) => ({ default: m.SignIn }))),
  forgot: lazy(() => import("./pages/auth/AuthPages").then((m) => ({ default: m.ForgotPassword }))),
  reset: lazy(() => import("./pages/auth/AuthPages").then((m) => ({ default: m.ResetPassword }))),
  verify: lazy(() => import("./pages/auth/AuthPages").then((m) => ({ default: m.VerifyEmail }))),
  invite: lazy(() => import("./pages/auth/AuthPages").then((m) => ({ default: m.AcceptInvite }))),
  onboarding: lazy(() => import("./pages/onboarding/Onboarding")),
  enquiry: lazy(() => import("./pages/enquiry/Enquiry")),
  invoice: lazy(() => import("./pages/invoice/PublicInvoice")),
  booking: lazy(() => import("./pages/booking/PublicBooking")),
  mediaKit: lazy(() => import("./pages/mediakit/PublicMediaKit")),
  creators: lazy(() => import("./pages/landing/Creators")),
  app: lazy(() => import("./pages/app/AppShell")),
  demo: lazy(() => import("./pages/app/AppShell").then((m) => ({ default: m.DemoShell }))),
  community: lazy(() => import("./pages/community/Community")),
  setupCalls: lazy(() => import("./pages/admin/SetupCalls")),
  legal: lazy(() => import("./pages/legal/Legal")),
  notFound: lazy(() => import("./pages/legal/Legal").then((m) => ({ default: m.NotFound }))),
};

type Page = LazyExoticComponent<ComponentType<{ params: Record<string, string> }>> | ComponentType<{ params: Record<string, string> }>;

const routes: [string, Page][] = [
  ["/", Landing as Page],
  ["/signup", pages.signUp as Page],
  ["/signin", pages.signIn as Page],
  ["/forgot-password", pages.forgot as Page],
  ["/reset-password", pages.reset as Page],
  ["/verify-email", pages.verify as Page],
  ["/invite", pages.invite as Page],
  ["/onboarding", pages.onboarding as Page],
  ["/e/:key", pages.enquiry as Page],
  ["/i/:token", pages.invoice as Page],
  ["/b/:key", pages.booking as Page],
  ["/m/:slug", pages.mediaKit as Page],
  ["/creators", pages.creators as Page],
  ["/privacy", pages.legal as Page],
  ["/terms", pages.legal as Page],
  ["/contact", pages.legal as Page],
  ["/refunds", pages.legal as Page],
  ["/community", pages.community as Page],
  ["/community/new", pages.community as Page],
  ["/community/:id", pages.community as Page],
  ["/admin/setup-calls", pages.setupCalls as Page],
  ["/demo", pages.demo as Page],
  ["/demo/:section", pages.demo as Page],
  ["/demo/:section/:id", pages.demo as Page],
  ["/app", pages.app as Page],
  ["/app/:section", pages.app as Page],
  ["/app/:section/:id", pages.app as Page],
];

function Router() {
  const path = usePath();
  for (const [pattern, Component] of routes) {
    const params = match(pattern, path);
    if (params) return <Component params={params} />;
  }
  const NotFound = pages.notFound;
  return <NotFound />;
}

function PageLoading() {
  return (
    <div className="page-loading" role="status">
      <span className="spinner" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export default function App() {
  return (
    <SessionProvider>
      <ToastProvider>
        <Suspense fallback={<PageLoading />}>
          <Router />
        </Suspense>
      </ToastProvider>
    </SessionProvider>
  );
}
